import { ValidationErrors } from '@angular/forms';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faCircleX, faHand } from '@fortawesome/pro-solid-svg-icons';
import { DEFAULT_NUMERIC_MASK, ISelectOption, ITranslatableError } from '@swissfex/sfx-commons';
import { de, enGB, frCH, itCH } from 'date-fns/locale';
import { de_DE, en_US, fr_FR, it_IT } from 'ng-zorro-antd/i18n';

import { IHdCdpFieldState } from '../hypo-dossier/cdp';
import { BorrowerType } from '../projects/models/enums/borrowerType';
import { BusinessType } from '../projects/models/enums/businessType';
import { ObjectType } from '../projects/models/enums/objectType';
import { OfferStatus } from '../projects/models/enums/offerStatus';
import { ProductType } from '../projects/models/enums/productType';
import { ProjectStatus } from '../projects/models/enums/projectStatus';
import { ValuationQualityIAZI } from '../projects/models/enums/valuationQualityIAZI';
import { ValuationQualityWP } from '../projects/models/enums/valuationQualityWP';
import { PricingVariationKeys } from '../projects/models/IPricingVariation';
import { IProject } from '../projects/models/IProject';

export * from './select-option-constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export declare type SafeAny = any;

export const DEFAULT_COORDS = {
    LON: 8.5417,
    LAT: 47.3769
};

export const DEFAULT_CREATE_PROJECT_FORM_VALUE = {
    title: undefined,
    businessType: BusinessType.Purchase,
    objectType: ObjectType.Condominium
};

export const DEFAULT_HD_CDP_FIELD_STATE: IHdCdpFieldState = {
    count: 0,
    isLoading: false,
    recommendationType: null,
    values: [],
    hdfRecommendationId: null
};

export const DATE_HOUR_FORMAT = 'dd.MM.yyyy - HH:mm';

export const DEFAULT_CURRENCY = 'CHF';

export const DEFAULT_DECIMAL_NUMERIC_MASK: SafeAny = {
    mask: Number,
    scale: 1,
    min: 0,
    padFractionalZeros: false,
    normalizeZeros: true,
    radix: '.',
    mapToRadix: ['.']
};

export const DEFAULT_INT_NUMERIC_MASK: SafeAny = {
    mask: Number,
    scale: 0,
    min: 0,
    padFractionalZeros: false,
    normalizeZeros: true
};

export const SIGNED_DEFAULT_NUMERIC_MASK: SafeAny = {
    ...DEFAULT_NUMERIC_MASK,
    min: Number.MIN_SAFE_INTEGER
};

export const SIGNED_DECIMAL_MASK: SafeAny = {
    ...SIGNED_DEFAULT_NUMERIC_MASK,
    scale: 2
};

export const YEAR_NUMERIC_MASK: SafeAny = {
    ...DEFAULT_INT_NUMERIC_MASK,
    max: 9999
};

export const OWNERSHIP_PERCENTAGE_MASK: SafeAny = {
    ...DEFAULT_INT_NUMERIC_MASK,
    max: 1000
};

export const US_SOCIAL_SECURITY_NUMBER_MASK = '000-00-0000';

export const CLOSEST_PAYOUT_DATE_DELAY_IN_MINUTES = 5;
export const DEFAULT_PAGE_THUMBNAIL_WIDTH = 400;

export const DEFAULT_NOT_APPLICABLE_TRANCHE_RUNTIME = {
    [ProductType.Fixed]: 10,
    [ProductType.Saron]: null,
    [ProductType.Variable]: null,
    [ProductType.ConstructionCredit]: null
};

export const AUTH_ROUTES = {
    BACKOFFICE: 'bo'
};

export const MAIN_ROUTES = {
    ABOUT_US: 'about-us',
    HOME: 'home',
    KEY4_TRAINING_COMPLETED: 'key4-training',
    LOGGING_IN: 'logging-in',
    LOGIN: 'login',
    LOGIN_BACKOFFICE_v2: `login/${AUTH_ROUTES.BACKOFFICE}`,
    LOGIN_BACKOFFICE: `${AUTH_ROUTES.BACKOFFICE}/login`,
    LOGOUT: 'logout',
    PROJECTS: 'projects'
};

export const PROJECT_PATHS = {
    EDIT: 'edit'
};

export const EDIT_PROJECT_PATHS = {
    ASSIGN_DOCUMENT: 'assign-document',
    BORROWER: 'borrower',
    BORROWER_ADDITIONAL_INCOME_RENTAL_INCOME: 'rental-income',
    BORROWER_ASSETS: 'assets',
    BORROWER_EMPLOYMENT: 'employment',
    BORROWER_LEGAL_ENTITY: 'legal-entity',
    BORROWER_LEGAL_ENTITY_COMPANY_DETAILS: 'company-details',
    BORROWER_LIABILITIES_CONSUMER_CREDIT: 'consumer-credit',
    BORROWER_LIABILITIES_THIRD_PARTY_LOAN: 'third-party-loan',
    BORROWER_PERSONAL_INFO: 'personal-info',
    BORROWER_SECURITIES_INCOME: 'securities-income',
    BORROWER_SPECIAL_CASES: 'special-cases',
    DESCRIPTION: 'description',
    DOCUMENTS: 'documents',
    REQUEST: 'request',
    OFFERS: 'offers',
    OFFER_DETAILS_DRAWER: 'offer',
    OFFER_COMPARISON: 'offer-comparison',
    TEAM_SELLING: 'team-selling'
};

export const SIDEBAR_OUTLET_PATHS = {
    SERVICE_CENTER: 'service-center',
    SETTINGS: 'settings',
    NEWS: 'news'
};

export const FINANCING_DRAWER_PATHS = {
    FINANCING: 'financing',
    BORROWER_ASSETS: 'assets'
};

export const DOCUMENT_VERSIONS_MODAL_PATH = 'document-versions';

export const PROPERTY_MODAL_PATHS = {
    FULL_VALUATION: 'full-valuation',
    LIGHT_VALUATION: 'light-valuation',
    PROPERTY: 'property',
    SPECIAL_CASES: 'special-cases'
};

export const PROJECT_PAGE_URL_SUBITEMS_TEMPLATE = [MAIN_ROUTES.PROJECTS, '{projectId}'];
export const PROJECT_DOCUMENTS_PAGE_URL_SUBITEMS_TEMPLATE = [
    ...PROJECT_PAGE_URL_SUBITEMS_TEMPLATE,
    EDIT_PROJECT_PATHS.DOCUMENTS
];
export const REQUEST_PAGE_URL_SUBITEMS_TEMPLATE = [...PROJECT_PAGE_URL_SUBITEMS_TEMPLATE, EDIT_PROJECT_PATHS.REQUEST];
export const OFFERS_PAGE_URL_SUBITEMS_TEMPLATE = [...PROJECT_PAGE_URL_SUBITEMS_TEMPLATE, EDIT_PROJECT_PATHS.OFFERS];

export enum OUTLET_NAMES {
    BORROWER_ADDITIONAL_INCOME_RENTAL_INCOME_MODAL_OUTLET = 'ri',
    BORROWER_ASSETS_MODAL_OUTLET = 'a',
    BORROWER_LEGAL_ENTITY_MODAL = 'ble',
    BORROWER_LEGAL_ENTITY_COMPANY_DETAILS_MODAL_OUTLET = 'blecd',
    BORROWER_LIABILITIES_CONSUMER_CREDIT_MODAL_OUTLET = 'cc',
    BORROWER_LIABILITIES_THIRD_PARTY_LOAN_MODAL_OUTLET = 'btpl',
    BORROWER_MAIN_EMPLOYMENT_MODAL_OUTLET = 'me',
    BORROWER_MODAL_OUTLET = 'bor',
    BORROWER_PERSONAL_INFO_MODAL_OUTLET = 'bpi',
    BORROWER_SECONDARY_EMPLOYMENT_MODAL_OUTLET = 'se',
    BORROWER_SECURITIES_INCOME_MODAL_OUTLET = 'si',
    BORROWER_SPECIAL_CASES_MODAL_OUTLET = 'bsc',

    DOCUMENT_MODAL_OUTLET = 'doc',
    DOCUMENT_VERSIONS_MODAL_OUTLET = 'dv',

    FINANCING_DRAWER_OUTLET = 'findr',

    NEWS_MODAL_OUTLET = 'n',
    OFFER_DETAILS_DRAWER_OUTLET = 'od',
    OFFER_COMPARISON_OUTLET = 'oc',

    PROJECT_DESCRIPTION_OUTLET = 'desc',

    PROPERTY_MODAL_OUTLET = 'pro',
    PROPERTY_FULL_VALUATION_MODAL_OUTLET = 'pfv',
    PROPERTY_LIGHT_VALUATION_MODAL_OUTLET = 'plv',
    PROPERTY_SPECIAL_CASES_OUTLET = 'prspc',

    SERVICE_CENTER_MODAL_OUTLET = 'sc',
    SETTINGS_MODAL_OUTLET = 'cfg',

    TEAM_SELLING_MODAL_OUTLET = 'ts'
}

export const PATH_NAMES_PER_OUTLETS = {
    [OUTLET_NAMES.BORROWER_ADDITIONAL_INCOME_RENTAL_INCOME_MODAL_OUTLET]:
        EDIT_PROJECT_PATHS.BORROWER_ADDITIONAL_INCOME_RENTAL_INCOME,
    [OUTLET_NAMES.BORROWER_ASSETS_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER_ASSETS,
    [OUTLET_NAMES.BORROWER_LEGAL_ENTITY_MODAL]: EDIT_PROJECT_PATHS.BORROWER_LEGAL_ENTITY,
    [OUTLET_NAMES.BORROWER_LEGAL_ENTITY_COMPANY_DETAILS_MODAL_OUTLET]:
        EDIT_PROJECT_PATHS.BORROWER_LEGAL_ENTITY_COMPANY_DETAILS,
    [OUTLET_NAMES.BORROWER_LIABILITIES_CONSUMER_CREDIT_MODAL_OUTLET]:
        EDIT_PROJECT_PATHS.BORROWER_LIABILITIES_CONSUMER_CREDIT,
    [OUTLET_NAMES.BORROWER_LIABILITIES_THIRD_PARTY_LOAN_MODAL_OUTLET]:
        EDIT_PROJECT_PATHS.BORROWER_LIABILITIES_THIRD_PARTY_LOAN,
    [OUTLET_NAMES.BORROWER_MAIN_EMPLOYMENT_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER_EMPLOYMENT,
    [OUTLET_NAMES.BORROWER_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER,
    [OUTLET_NAMES.BORROWER_PERSONAL_INFO_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER_PERSONAL_INFO,
    [OUTLET_NAMES.BORROWER_SECONDARY_EMPLOYMENT_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER_EMPLOYMENT,
    [OUTLET_NAMES.BORROWER_SECURITIES_INCOME_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER_SECURITIES_INCOME,
    [OUTLET_NAMES.BORROWER_SPECIAL_CASES_MODAL_OUTLET]: EDIT_PROJECT_PATHS.BORROWER_SPECIAL_CASES,

    [OUTLET_NAMES.DOCUMENT_MODAL_OUTLET]: EDIT_PROJECT_PATHS.DOCUMENTS,
    [OUTLET_NAMES.DOCUMENT_VERSIONS_MODAL_OUTLET]: DOCUMENT_VERSIONS_MODAL_PATH,

    [OUTLET_NAMES.FINANCING_DRAWER_OUTLET]: FINANCING_DRAWER_PATHS.FINANCING,

    [OUTLET_NAMES.NEWS_MODAL_OUTLET]: SIDEBAR_OUTLET_PATHS.NEWS,

    [OUTLET_NAMES.OFFER_DETAILS_DRAWER_OUTLET]: EDIT_PROJECT_PATHS.OFFER_DETAILS_DRAWER,
    [OUTLET_NAMES.OFFER_COMPARISON_OUTLET]: EDIT_PROJECT_PATHS.OFFER_COMPARISON,

    [OUTLET_NAMES.PROJECT_DESCRIPTION_OUTLET]: EDIT_PROJECT_PATHS.DESCRIPTION,

    [OUTLET_NAMES.PROPERTY_MODAL_OUTLET]: PROPERTY_MODAL_PATHS.PROPERTY,
    [OUTLET_NAMES.PROPERTY_FULL_VALUATION_MODAL_OUTLET]: PROPERTY_MODAL_PATHS.FULL_VALUATION,
    [OUTLET_NAMES.PROPERTY_LIGHT_VALUATION_MODAL_OUTLET]: PROPERTY_MODAL_PATHS.LIGHT_VALUATION,
    [OUTLET_NAMES.PROPERTY_SPECIAL_CASES_OUTLET]: PROPERTY_MODAL_PATHS.SPECIAL_CASES,

    [OUTLET_NAMES.TEAM_SELLING_MODAL_OUTLET]: EDIT_PROJECT_PATHS.TEAM_SELLING,
    [OUTLET_NAMES.SERVICE_CENTER_MODAL_OUTLET]: SIDEBAR_OUTLET_PATHS.SERVICE_CENTER,
    [OUTLET_NAMES.SETTINGS_MODAL_OUTLET]: SIDEBAR_OUTLET_PATHS.SETTINGS
};

export const FIXED_TRANCHE_RUNTIME_OPTIONS = {
    MIN: 1,
    MAX: 25
};

export enum SUPPORTED_LANGUAGE {
    DE_CH = 'DE_CH',
    EN = 'EN',
    FR_CH = 'FR_CH',
    IT_CH = 'IT_CH'
}

export const DATE_FORMATS_PER_LANGUAGE = {
    [SUPPORTED_LANGUAGE.DE_CH]: ['dd.MM.yyyy', 'd.M.yyyy', 'dd.M.yyyy', 'd.MM.yyyy'],
    [SUPPORTED_LANGUAGE.EN]: ['dd.MM.yyyy', 'd.M.yyyy', 'dd.M.yyyy', 'd.MM.yyyy'],
    [SUPPORTED_LANGUAGE.FR_CH]: ['dd.MM.yyyy', 'd.M.yyyy', 'dd.M.yyyy', 'd.MM.yyyy'],
    [SUPPORTED_LANGUAGE.IT_CH]: ['dd.MM.yyyy', 'd.M.yyyy', 'dd.M.yyyy', 'd.MM.yyyy']
};

export const DATE_TIME_FORMATS_PER_LANGUAGE = {
    [SUPPORTED_LANGUAGE.DE_CH]: ['dd.MM.yyyy HH:mm', 'd.M.yyyy HH:mm', 'dd.M.yyyy HH:mm', 'd.MM.yyyy HH:mm'],
    [SUPPORTED_LANGUAGE.EN]: ['dd.MM.yyyy HH:mm', 'd.M.yyyy HH:mm', 'dd.M.yyyy HH:mm', 'd.MM.yyyy HH:mm'],
    [SUPPORTED_LANGUAGE.FR_CH]: ['dd.MM.yyyy HH:mm', 'd.M.yyyy HH:mm', 'dd.M.yyyy HH:mm', 'd.MM.yyyy HH:mm'],
    [SUPPORTED_LANGUAGE.IT_CH]: ['dd.MM.yyyy HH:mm', 'd.M.yyyy HH:mm', 'dd.M.yyyy HH:mm', 'd.MM.yyyy HH:mm']
};

export const DATE_MONTH_FORMATS_PER_LANGUAGE = {
    [SUPPORTED_LANGUAGE.DE_CH]: ['MM.yyyy', 'M.yyyy'],
    [SUPPORTED_LANGUAGE.EN]: ['MM.yyyy', 'M.yyyy'],
    [SUPPORTED_LANGUAGE.FR_CH]: ['MM.yyyy', 'M.yyyy'],
    [SUPPORTED_LANGUAGE.IT_CH]: ['MM.yyyy', 'M.yyyy']
};

export const DateFnsLocales = {
    [SUPPORTED_LANGUAGE.EN]: enGB,
    [SUPPORTED_LANGUAGE.DE_CH]: de,
    [SUPPORTED_LANGUAGE.IT_CH]: itCH,
    [SUPPORTED_LANGUAGE.FR_CH]: frCH
};

export const SWISS_NATIONALITY_ALPHA2_CODE = 'CH';

export const LANGUAGE_FILE_NAME_SUFFIX = {
    [SUPPORTED_LANGUAGE.DE_CH]: 'de-ch',
    [SUPPORTED_LANGUAGE.EN]: 'en',
    [SUPPORTED_LANGUAGE.FR_CH]: 'fr-ch',
    [SUPPORTED_LANGUAGE.IT_CH]: 'it-ch'
};

export const I18N_LANGUAGE_FILE_NAME = {
    [SUPPORTED_LANGUAGE.DE_CH]: de_DE,
    [SUPPORTED_LANGUAGE.EN]: en_US,
    [SUPPORTED_LANGUAGE.FR_CH]: fr_FR,
    [SUPPORTED_LANGUAGE.IT_CH]: it_IT
};

export const IAZI_CULTURES = {
    [SUPPORTED_LANGUAGE.DE_CH]: 'de-CH',
    [SUPPORTED_LANGUAGE.EN]: 'en-US',
    [SUPPORTED_LANGUAGE.FR_CH]: 'fr-CH',
    [SUPPORTED_LANGUAGE.IT_CH]: 'it-CH'
};

export const ISO_2_LANGUAGES = {
    [LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.EN]]: 'en',
    [LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.FR_CH]]: 'fr',
    [LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.DE_CH]]: 'de',
    [LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.IT_CH]]: 'it'
};

export const HYPO_DOSSIER_LANGUAGES = { ...ISO_2_LANGUAGES };

export const EXTENDED_SUPPORTED_LANGUAGES = {
    'de-DE': LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.DE_CH],
    'en-US': LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.EN],
    'it-IT': LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.IT_CH],
    'fr-FR': LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.FR_CH]
};

export const LANGUAGE_OPTIONS: ISelectOption[] = [
    { value: LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.DE_CH], label: 'languages.german' },
    { value: LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.FR_CH], label: 'languages.french' },
    { value: LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.IT_CH], label: 'languages.italian' },
    { value: LANGUAGE_FILE_NAME_SUFFIX[SUPPORTED_LANGUAGE.EN], label: 'languages.english' }
];

export enum PopupNotificationType {
    success = 'success',
    info = 'info',
    warning = 'warning',
    error = 'error'
}

export enum OfferState {
    DISABLED = 'DISABLED',
    SINGLE_SELECT = 'SINGLE_SELECT',
    MULTI_SELECT = 'MULTI_SELECT',
    HIDDEN = 'HIDDEN'
}

export const OFFER_SELECTION_SWITCH_TYPES = {
    FIXABLE_TO_FIXABLE: 'FIXABLE_TO_FIXABLE',
    FIXABLE_TO_FIXABLE_FROM_SAME_LENDER: 'FIXABLE_TO_FIXABLE_FROM_SAME_LENDER',
    FIXABLE_TO_SUBMITTABLE_SINGLE_SELECTION: 'FIXABLE_TO_SUBMITTABLE_SINGLE_SELECTION',
    FIXABLE_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER:
        'FIXABLE_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER',
    FIXABLE_TO_SUBMITTABLE: 'FIXABLE_TO_SUBMITTABLE',
    FIXABLE_TO_SUBMITTABLE_FROM_SAME_LENDER: 'FIXABLE_TO_SUBMITTABLE_FROM_SAME_LENDER',
    SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Good: 'SUBMITTABLE_TO_SUBMITTABLE',
    SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Low: 'SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Low',
    SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_VeryLow: 'SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_VeryLow',
    SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Catastrophic:
        'SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Catastrophic',
    SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Beginner:
        'SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Beginner',
    SUBMITTABLE_TO_SUBMITTABLE_FROM_SAME_LENDER: 'SUBMITTABLE_TO_SUBMITTABLE_FROM_SAME_LENDER',
    SUBMITTABLE_TO_SUBMITTABLE_FROM_SAME_PARTNER: 'SUBMITTABLE_TO_SUBMITTABLE_FROM_SAME_PARTNER',
    SUBMITTABLE_TO_FIXABLE: 'SUBMITTABLE_TO_FIXABLE',
    SUBMITTABLE_TO_FIXABLE_FROM_SAME_LENDER: 'SUBMITTABLE_TO_FIXABLE_FROM_SAME_LENDER',
    SUBMITTABLE_TO_SUBMITTABLE_SINGLE_SELECTION: 'SUBMITTABLE_TO_SUBMITTABLE_SINGLE_SELECTION',
    SUBMITTABLE_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER:
        'SUBMITTABLE_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER',
    SUBMITTABLE_SINGLE_SELECTION_TO_FIXABLE: 'SUBMITTABLE_SINGLE_SELECTION_TO_FIXABLE',
    SUBMITTABLE_SINGLE_SELECTION_TO_FIXABLE_FROM_SAME_LENDER:
        'SUBMITTABLE_SINGLE_SELECTION_TO_FIXABLE_FROM_SAME_LENDER',
    SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_SINGLE_SELECTION:
        'SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_SINGLE_SELECTION',
    SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER:
        'SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER',
    SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE: 'SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE',
    SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_FROM_SAME_LENDER:
        'SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_FROM_SAME_LENDER'
};

export const OFFER_SELECTION_SWITCH_KEYS = {
    [OFFER_SELECTION_SWITCH_TYPES.FIXABLE_TO_FIXABLE]: 'project.offer.fixable_to_fixable',
    [OFFER_SELECTION_SWITCH_TYPES.FIXABLE_TO_FIXABLE_FROM_SAME_LENDER]:
        'project.offer.fixable_to_fixable_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.FIXABLE_TO_SUBMITTABLE_SINGLE_SELECTION]:
        'project.offer.fixable_to_submittable_single_selection',
    [OFFER_SELECTION_SWITCH_TYPES.FIXABLE_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER]:
        'project.offer.fixable_to_submittable_single_selection_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.FIXABLE_TO_SUBMITTABLE]: 'project.offer.fixable_to_submittable',
    [OFFER_SELECTION_SWITCH_TYPES.FIXABLE_TO_SUBMITTABLE_FROM_SAME_LENDER]:
        'project.offer.fixable_to_submittable_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_FIXABLE]: 'project.offer.submittable_to_fixable',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_FIXABLE_FROM_SAME_LENDER]:
        'project.offer.submittable_to_fixable_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_SINGLE_SELECTION]:
        'project.offer.submittable_to_submittable_single_selection',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER]:
        'project.offer.submittable_to_submittable_single_selection_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Good]:
        'project.offer.submittable_to_submittable',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Low]:
        'project.offer.submittable_to_submittable_with_conversion_rate_low',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_VeryLow]:
        'project.offer.submittable_to_submittable_with_conversion_rate_very_low',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Catastrophic]:
        'project.offer.submittable_to_submittable_with_conversion_rate_catastrophic',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_WITH_CONVERSION_RATE_Beginner]:
        'project.offer.submittable_to_submittable_with_conversion_rate_beginner',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_FROM_SAME_LENDER]:
        'project.offer.submittable_to_submittable_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_TO_SUBMITTABLE_FROM_SAME_PARTNER]:
        'project.offer.submittable_to_submittable_from_same_partner',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_SINGLE_SELECTION_TO_FIXABLE]:
        'project.offer.submittable_single_selection_to_fixable',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_SINGLE_SELECTION_TO_FIXABLE_FROM_SAME_LENDER]:
        'project.offer.submittable_single_selection_to_fixable_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_SINGLE_SELECTION]:
        'project.offer.submittable_single_selection_to_submittable_single_selection',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_SINGLE_SELECTION_FROM_SAME_LENDER]:
        'project.offer.submittable_single_selection_to_submittable_single_selection_from_same_lender',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE]:
        'project.offer.submittable_single_selection_to_submittable',
    [OFFER_SELECTION_SWITCH_TYPES.SUBMITTABLE_SINGLE_SELECTION_TO_SUBMITTABLE_FROM_SAME_LENDER]:
        'project.offer.submittable_single_selection_to_submittable_from_same_lender'
};

export enum ProjectStatusTableSizeOptions {
    Small = 'Small',
    Large = 'Large'
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const EMPTY_KEYPRESS_FUNC = (event) => true;

export const UPLOAD_STATUSES = {
    PROGRESS: 'progress',
    RESPONSE: 'response'
};

export const OFFERS_STEP = {
    NO_OFFERS: 'NO_OFFERS',
    BOUND_OFFERS: 'BOUND_OFFERS',
    AMENDED_OFFERS: 'AMENDED_OFFERS',
    ACCEPTED_OFFER: 'ACCEPTED_OFFER',
    FIXED_OFFER: 'FIXED_OFFER'
};

export const LOCATION_AUTOCOMPLETE_FIELDS = {
    STREET: 'streetname',
    HOUSE_NUMBER: 'houseNumber',
    ZIP: 'zipcode',
    TOWN: 'townName'
};

export const LOCATION_SERVICE_CHECK_TIMEOUT = 30000;

export const UNBOUND_OFFERS_PROJECT_STATUSES = [
    ProjectStatus.Open,
    ProjectStatus.LightOfferCreated,
    ProjectStatus.OfferOverviewCreated
];

export const OFFER_CHANGED_STATUSES = [
    OfferStatus.AcceptedByLender,
    OfferStatus.RejectedByLender,
    OfferStatus.AmendedByLender
];

export const DEFAULT_ICONS = {
    MORE_INFO: faCircleInfo,
    EDIT_ICON: faPenToSquare,
    CLEAR_INPUT: faCircleX,
    ALERT_ICON: faHand
};

export enum LegacyIdentityProvider {
    SwissLife = 0,
    DfsIdentityServer = 3
}

export enum IdentityProvider {
    SwissLife = 'SwissLife',
    DfsIdentityServer = 'DfsIdentityServer'
}

export enum BorrowerAssetsLocation {
    Borrower,
    Financing
}

export enum BorrowerEmploymentType {
    Main,
    Secondary
}

export const FILE_TYPES = {
    FILE: 'FILE',
    VIRTUAL: 'VIRTUAL'
};

export const VIRTUAL_FILE_MIME = 'application/x-dnd-move';

export const IAZI_PROP_TRANSLATION_KEYS = (objectType: ObjectType): Record<string, string> => ({
    zip: 'property.zip',
    town: 'property.town',
    street: 'property.street',
    ortId: 'property.town',
    bathNb: 'property.full_valuation.number_of_regular_bathrooms',
    buildCondition: 'property.full_valuation.building_condition',
    buildQuality: '',
    buildRightDate: 'property.special_cases.leasehold_expiry_date',
    buildYear: 'property.full_valuation.build_year',
    flatCondition: 'property.light_valuation.flat_condition',
    flatNb: 'property.full_valuation.total_number_of_flats',
    floor: 'property.full_valuation.floor',
    floorNb: 'property.full_valuation.total_number_of_floors',
    liftNb: '',
    luxusFlag: 'property.full_valuation.is_luxury_property',
    maisonetteFlag: '',
    minergieCode: 'property.full_valuation.minergie_code',
    ownershipPercent: 'property.full_valuation.ownership_percentage',
    parkCoverNb: 'property.full_valuation.parking.' + objectType + '.number_of_garages_separate_building',
    parkExternalNb: 'property.full_valuation.parking.' + objectType + '.number_of_garages_outside_the_property',
    parkOpenNb: 'property.full_valuation.parking.' + objectType + '.has_outdoor_parking_spaces',
    primSecFlag: '',
    qualityFlatHouse: '',
    qualityMicro: '',
    renovationYear: 'property.full_valuation.year',
    roofFlag: '',
    roomNb: 'property.full_valuation.number_of_rooms',
    roomShiftCode: '',
    sepWcNb: 'property.full_valuation.number_of_guest_bathrooms',
    servituteFlag: 'property.special_cases.servitute_flag',
    surfaceBalcony: 'property.full_valuation.surface_balcony',
    surfaceGround: 'property.full_valuation.surface_land',
    surfaceLiving: 'property.full_valuation.surface_living_net',
    typeFlatBuildingCode: '',
    typeFlatCode: '',
    typeHouseCode: 'property.light_valuation.house_type',
    volume: 'property.full_valuation.volume',
    volumeCode: 'property.full_valuation.volume_code'
});

export const SPECIFIC_DOCUMENT_GROUPS = {
    NotCategorized: 'NotCategorized',
    NotUsed: 'NotUsed'
};

export const SwissLifePensionInstitution = 'SwissLife';

export const DOCUMENTS_FOOTER_ACTION = {
    SubmitToCC: 'SubmitToCC',
    SubmitToLender: 'SubmitToLender',
    ReleaseProject: 'ReleaseProject'
};

export const CANCELLED_REQUEST_ERROR = 'The request was cancelled.';

export const CANCELLED_REQUEST_DUE_TO_INCOMPLETE_DATA = 'The request was cancelled due to incomplete data.';

export const DefaultEmptyLenderId = -1;

export const TextEndingWithParenthesesRegex = /^(.*\S)\s+\(([^)]*)\)$/;
export const PrivateTextRegex = /%PRIVATE%(.*?)%PRIVATE%/g;

export enum LOGOUT_REASON {
    UNAUTHORIZED = '401',
    SESSION_EXPIRED = 'session-expired',
    FORBIDDEN = '403',
    USER_LOGOUT = 'user_logout',
    SESSION_PROLONG = 'session_prolong'
}

export enum LOGIN_QUERY {
    ERROR = 'err',
    LOGOUT = 'reason',
    RETURN_URL = 'returnUrl'
}

export const LOGIN_QUERY_LOGOUT_REASON_MAP: Record<LOGOUT_REASON, LOGIN_QUERY> = {
    [LOGOUT_REASON.UNAUTHORIZED]: LOGIN_QUERY.ERROR,
    [LOGOUT_REASON.SESSION_EXPIRED]: LOGIN_QUERY.ERROR,
    [LOGOUT_REASON.FORBIDDEN]: LOGIN_QUERY.ERROR,
    [LOGOUT_REASON.USER_LOGOUT]: LOGIN_QUERY.LOGOUT,
    [LOGOUT_REASON.SESSION_PROLONG]: LOGIN_QUERY.LOGOUT
};

enum PROPERTY_VALUATION_QUALITY_COLOURS {
    GREEN = '#6cd5c6',
    RED = '#ff7b7b',
    YELLOW = 'yellow'
}

export const PROPERTY_VALUATION_QUALITY_COLORS_IAZI: Record<ValuationQualityIAZI, PROPERTY_VALUATION_QUALITY_COLOURS> =
    {
        [ValuationQualityIAZI.Extrapolated]: PROPERTY_VALUATION_QUALITY_COLOURS.RED,
        [ValuationQualityIAZI.Prediction]: PROPERTY_VALUATION_QUALITY_COLOURS.RED,
        [ValuationQualityIAZI.Fair]: PROPERTY_VALUATION_QUALITY_COLOURS.YELLOW,
        [ValuationQualityIAZI.Good]: PROPERTY_VALUATION_QUALITY_COLOURS.GREEN
    };

export const PROPERTY_VALUATION_QUALITY_COLORS_WP: Record<ValuationQualityWP, PROPERTY_VALUATION_QUALITY_COLOURS> = {
    [ValuationQualityWP.Green]: PROPERTY_VALUATION_QUALITY_COLOURS.GREEN,
    [ValuationQualityWP.Yellow]: PROPERTY_VALUATION_QUALITY_COLOURS.YELLOW,
    [ValuationQualityWP.Red]: PROPERTY_VALUATION_QUALITY_COLOURS.RED
};

export const PROPERTY_VALUATION_PROVIDERS = {
    IAZI: 'IAZI',
    WP: 'Wüest Partner'
};

export enum AuthServiceConfigType {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT'
}

export const CRM_CUSTOMER_ID_QUERY = 'uuid';

export const IGNORED_PROJECT_MUTATION_PATHS: (string | RegExp)[] = [
    '/lastModifiedDate',
    /^\/submissionData\//,
    /^\/generatedDocuments\/\d+\/actionsAllowed$/,
    '/versionDescription',
    /^\/projectVersions\//
];

export const SKELETON_PROJECT = {
    borrowersData: {
        borrowersType: BorrowerType.Individual
    },
    permissions: []
} as IProject;

export const SIDEBAR_NAVIGATION_ITEMS_TITLE = {
    description: 'navigation_title.description',
    documentsModal: 'navigation_title.documents',
    documentsPage: 'edit_project.documents',
    feedback: 'navigation_title.feedback',
    home: 'navigation_title.home',
    logout: 'navigation_title.logout',
    news: 'navigation_title.news',
    presenterView: 'navigation_title.presenter_view',
    serviceCenter: 'navigation_title.service_center',
    settings: 'navigation_title.settings',
    teamSelling: 'navigation_title.team_selling'
};

export const SIDEBAR_NAVIGATION_COMPACT_TRIGGER_WIDTH = 1680;

export const DOCUMENT_UPLOAD_FORM_DATA_KEYS = {
    ProjectId: 'ProjectId',
    FileContent: 'FileContent',
    Name: 'Name',
    Group: 'Group',
    LenderId: 'LenderId',
    DocumentId: 'DocumentId',
    Size: 'Size',
    MimeType: 'MimeType'
};

export const PRICING_VARIATION_INTEREST_RATE_LABEL = {
    [PricingVariationKeys.interestRate]: 'project.offer_details.pricing_variations.interest_rate_p_a',
    [PricingVariationKeys.interestRateFlex]: 'project.offer_details.pricing_variations.interest_rate_p_a_flex',
    [PricingVariationKeys.saronLenderInterest]: 'project.offer_details.pricing_variations.interest_rate_p_a',
    [PricingVariationKeys.saronLenderInterestFlex]: 'project.offer_details.pricing_variations.interest_rate_p_a_flex',
    [PricingVariationKeys.interestRateHomeOption]:
        'project.offer_details.pricing_variations.interest_rate_p_a_home_option',
    [PricingVariationKeys.interestRateHomeOptionFlex]:
        'project.offer_details.pricing_variations.interest_rate_p_a_home_option_flex',
    [PricingVariationKeys.greenMortgageInterestRate]:
        'project.offer_details.pricing_variations.interest_rate_p_a_sustainable',
    [PricingVariationKeys.greenMortgageInterestRateFlex]:
        'project.offer_details.pricing_variations.interest_rate_p_a_sustainable_flex',
    [PricingVariationKeys.greenMortgageSaronLenderInterest]:
        'project.offer_details.pricing_variations.interest_rate_p_a_sustainable',
    [PricingVariationKeys.greenMortgageSaronLenderInterestFlex]:
        'project.offer_details.pricing_variations.interest_rate_p_a_sustainable_flex'
};

export const PRICING_VARIATION_INTEREST_RATE_FROM_LABEL = Object.fromEntries(
    Object.entries(PRICING_VARIATION_INTEREST_RATE_LABEL).map(([key, value]) => [
        key,
        value.replace('interest_rate_p_a', 'interest_rate_from_p_a')
    ])
);

export const ProjectVersionDescriptionMask = {
    mask: /^[\s\S]{0,25}$/
};

export const INVALID_SUB_MODAL_ERROR: ValidationErrors = {
    invalidSubModal: {
        translationKey: 'errors.invalid_submodal'
    } satisfies ITranslatableError
};
